import { useCallback, useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Gauge } from 'lucide-react';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiClient, formatApiErrorMessage } from '@/lib/api';
import { getErrorMessage } from '../../lib/utils';
import FunctionHelp, { type FunctionHelpContent } from './FunctionHelp';

interface RagPlusSettings {
  enabled: boolean;
  model_provider: string;
  model_name: string;
  flags: {
    query_expansion: boolean;
    hyde: boolean;
    parent_document: boolean;
    adaptive_topk: boolean;
    query_expansion_kill_switch: boolean;
  };
}

interface CacheThresholdInfo {
  tenant_value: number | null;
  global_value: number | null;
  default_value: number;
  effective_value: number;
  min: number;
  max: number;
  source: 'tenant' | 'global' | 'default';
}

interface EmbedderStatusResponse {
  current?: string | null;
  gemini_chat_model?: string | null;
  openai_chat_model?: string | null;
}

/**
 * Modello usato a runtime da riformulazione e risposta ipotetica: il modello
 * chat coerente con l'embedder (tenant_settings.gemini_chat_model /
 * openai_chat_model, letto da getRagChatModel). Si cambia nel passo 1.
 */
interface ChatModelInfo {
  embedderProvider: 'gemini' | 'openai' | null;
  model: string | null;
}

/**
 * Livelli della memoria delle risposte: niente numeri liberi. Una soglia bassa
 * fa riusare la risposta di una domanda diversa, quindi non si propone un
 * livello sotto quello di piattaforma.
 */
type CacheLevel = 'platform' | 'careful' | 'identical' | 'custom';
const CACHE_LEVEL_VALUE: Record<'careful' | 'identical', number> = { careful: 0.95, identical: 1 };

const RAG_PLUS_HELP: FunctionHelpContent = {
  whatItDoes: 'Quattro tecniche che migliorano la ricerca nei documenti prima della risposta. Si attivano tutte insieme con l\'interruttore.',
  points: [
    { label: 'Riformulazione', text: 'cerca anche con una variante della domanda (es. "come resetto la password" → "recupero credenziali").' },
    { label: 'Risposta ipotetica', text: 'scrive una bozza di risposta e cerca i passaggi più simili a quella.' },
    { label: 'Paragrafo intero', text: 'quando un passaggio è pertinente, passa all\'assistente anche la sezione che lo contiene.' },
    { label: 'Quantità adattiva', text: 'legge da 5 a 25 passaggi in base a quanto sono pertinenti.' },
  ],
  whenItRuns: 'A ogni domanda alla Knowledge Base che non trova già una risposta in memoria.',
  impact: 'Due brevi chiamate AI in più per domanda; in genere qualche decimo di secondo in più. Vale per tutti gli agenti del tenant, voce compresa.',
  notes: 'Il paragrafo intero vale per i documenti caricati con la ricerca avanzata attiva: i precedenti restano ricercabili come prima e per estenderla vanno ricaricati. Non si applica ai chatbot pubblici e alle ricerche filtrate per destinatari. Se la disattivi, i documenti caricati nel frattempo non avranno il paragrafo intero finché non li ricarichi.',
};

const CACHE_HELP: FunctionHelpContent = {
  whatItDoes: 'Conserva per 10 minuti le risposte date dalla Knowledge Base e le riusa per le domande con lo stesso significato.',
  points: [
    { label: 'Standard', text: 'usa il valore di piattaforma.' },
    { label: 'Prudente', text: 'riusa solo domande quasi identiche (somiglianza 0,95).' },
    { label: 'Solo domande identiche', text: 'nessun riuso per domande soltanto simili.' },
  ],
  whenItRuns: 'Prima della ricerca: se trova una domanda equivalente risponde subito.',
  impact: 'Risposte più rapide e meno chiamate AI. Un livello troppo permissivo rischia di riusare la risposta di una domanda diversa.',
  advice: 'Per contenuti normativi, prezzi o dati che cambiano spesso scegli "Prudente".',
};

function cacheLevelOf(info: CacheThresholdInfo): CacheLevel {
  if (info.tenant_value === null) return 'platform';
  if (info.tenant_value === CACHE_LEVEL_VALUE.careful) return 'careful';
  if (info.tenant_value === CACHE_LEVEL_VALUE.identical) return 'identical';
  return 'custom';
}

function formatThreshold(v: number): string {
  return v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

function unwrap<T>(res: { data?: unknown } | undefined): T | null {
  const outer = res?.data as { data?: T } | T | undefined;
  if (outer && typeof outer === 'object' && 'data' in outer) return (outer as { data?: T }).data ?? null;
  return (outer as T | undefined) ?? null;
}

export default function RAGPlusSection() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<RagPlusSettings | null>(null);
  const [chatModel, setChatModel] = useState<ChatModelInfo | null>(null);
  const [cacheThreshold, setCacheThreshold] = useState<CacheThresholdInfo | null>(null);
  const [thresholdSaving, setThresholdSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setSettings(unwrap<RagPlusSettings>(await apiClient.get('/ai-tools/rag-plus-settings')));
    } catch (err) {
      toast({ title: 'Errore caricamento RAG Plus', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchCacheThreshold = useCallback(async () => {
    try {
      setCacheThreshold(unwrap<CacheThresholdInfo>(await apiClient.get('/ai-tools/rag-cache-threshold')));
    } catch {
      // Non bloccante: senza dati la riga della memoria non viene mostrata.
      setCacheThreshold(null);
    }
  }, []);

  const fetchChatModel = useCallback(async () => {
    try {
      const status = unwrap<EmbedderStatusResponse>(await apiClient.get('/tenant/embedder-status'));
      const provider = status?.current === 'gemini' || status?.current === 'openai' ? status.current : null;
      const raw = provider === 'gemini' ? status?.gemini_chat_model : provider === 'openai' ? status?.openai_chat_model : null;
      setChatModel({ embedderProvider: provider, model: raw && raw.trim() ? raw.trim() : null });
    } catch {
      setChatModel(null);
    }
  }, []);

  useEffect(() => {
    void fetchSettings();
    void fetchCacheThreshold();
    void fetchChatModel();
  }, [fetchSettings, fetchCacheThreshold, fetchChatModel]);

  const handleToggle = async (enabled: boolean) => {
    if (!settings) return;
    setSaving(true);
    try {
      const d = unwrap<{ enabled: boolean; model_provider: string; model_name: string }>(
        await apiClient.put('/ai-tools/rag-plus-settings', {
          enabled,
          model_provider: settings.model_provider,
          model_name: settings.model_name,
        }),
      );
      if (d) {
        setSettings({
          ...settings,
          enabled: d.enabled,
          model_provider: d.model_provider,
          model_name: d.model_name,
          flags: { query_expansion: d.enabled, hyde: d.enabled, parent_document: d.enabled, adaptive_topk: d.enabled, query_expansion_kill_switch: d.enabled },
        });
      }
      toast({ title: enabled ? 'Ricerca avanzata attivata' : 'Ricerca avanzata disattivata' });
    } catch (err) {
      toast({ title: 'Errore salvataggio', description: formatApiErrorMessage(err, 'Errore salvataggio RAG Plus'), variant: 'destructive' });
      await fetchSettings();
    } finally {
      setSaving(false);
    }
  };

  const handleCacheLevel = async (level: string) => {
    if (!cacheThreshold || (level !== 'platform' && level !== 'careful' && level !== 'identical')) return;
    const threshold = level === 'platform' ? null : CACHE_LEVEL_VALUE[level];
    setThresholdSaving(true);
    try {
      await apiClient.put('/ai-tools/rag-cache-threshold', { threshold });
      toast({ title: 'Memoria delle risposte aggiornata' });
      await fetchCacheThreshold();
    } catch (err) {
      toast({ title: 'Errore salvataggio', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setThresholdSaving(false);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="p-6 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </CardContent></Card>
    );
  }
  if (!settings) return null;

  const enabled = settings.enabled;
  const platformValue = cacheThreshold ? (cacheThreshold.global_value ?? cacheThreshold.default_value) : null;
  const cacheLevel = cacheThreshold ? cacheLevelOf(cacheThreshold) : 'platform';

  return (
    <div className="space-y-3" data-testid="rag-plus-section-root">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-foreground">Ricerca avanzata</p>
                <Badge variant="outline" className={enabled ? 'text-xs border-emerald-500/30 text-emerald-700 dark:text-emerald-400' : 'text-xs text-muted-foreground'}>
                  {enabled ? 'Attiva' : 'Disattivata'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Prima di cercare, l&apos;assistente riformula la domanda e, quando trova un passaggio utile, legge anche il
                paragrafo intero attorno. Risposte più complete, con un piccolo costo in più per ogni domanda.
                Attiva per impostazione predefinita; vale per tutti gli agenti del tenant, voce compresa.
              </p>
            </div>
            <Switch
              checked={enabled}
              disabled={saving}
              onCheckedChange={(v) => { void handleToggle(v); }}
              aria-label="Ricerca avanzata nei documenti"
              data-testid="rag-plus-toggle"
            />
          </div>

          <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-2" data-testid="rag-plus-chat-model">
            <CheckCircle2 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              {chatModel?.embedderProvider ? (
                <>
                  Usa il modello chat della pipeline RAG:{' '}
                  <strong className="text-foreground">
                    {chatModel.embedderProvider === 'gemini' ? 'Gemini' : 'OpenAI'} / {chatModel.model ?? 'in impostazione'}
                  </strong>
                  . Si cambia nel passo 1, sotto l&apos;embedder.
                </>
              ) : (
                <>Usa il modello chat della pipeline RAG, che si imposta dopo aver scelto l&apos;embedder (passo 1).</>
              )}
            </p>
          </div>

          <FunctionHelp testId="rag-plus-details" content={RAG_PLUS_HELP} />
        </CardContent>
      </Card>

      {cacheThreshold && platformValue !== null && (
        <Card data-testid="rag-cache-card">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground flex items-center gap-2">
                <Gauge className="h-4 w-4 text-muted-foreground" />
                Memoria delle risposte
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Per 10 minuti l&apos;assistente riusa la risposta già data a una domanda con lo stesso significato:
                risposte più rapide e meno costi. Più il livello è prudente, meno risposte vengono riusate.
                La percentuale di riuso è in RAG Analytics.
              </p>
              <FunctionHelp testId="rag-cache-details" content={CACHE_HELP} />
            </div>
            <div className="sm:w-64 shrink-0">
              <Select value={cacheLevel} onValueChange={(v) => { void handleCacheLevel(v); }} disabled={thresholdSaving}>
                <SelectTrigger className="h-9 text-xs" data-testid="rag-cache-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="platform" className="text-xs">Standard (valore piattaforma {formatThreshold(platformValue)})</SelectItem>
                  <SelectItem value="careful" className="text-xs">Prudente: solo domande quasi identiche</SelectItem>
                  <SelectItem value="identical" className="text-xs">Solo domande identiche</SelectItem>
                  {cacheLevel === 'custom' && cacheThreshold.tenant_value !== null && (
                    <SelectItem value="custom" disabled className="text-xs">
                      Personalizzato ({formatThreshold(cacheThreshold.tenant_value)})
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
